type SuccessCallback<T, TResult1 = T, TResult2 = never> = (value: T) => TResult1 | PromiseLike<TResult1>;
type ErrorCallback<TResult2 = never> = (reason: any) => TResult2 | PromiseLike<TResult2>;

export class PromiseContainer {
  private _promises: Map<Promise<any>, CancellablePromise<any>> = new Map<Promise<any>, CancellablePromise<any>>();

  add<T>(pr: Promise<T>|CancellablePromise<T>): CancellablePromise<T> {
    let cancellablePromise: CancellablePromise<T>;
    if('cancel' in pr) {
      cancellablePromise = pr;
    } else {
      cancellablePromise = new WrappedCancellablePromise(pr);
    }
    this._promises.set(pr, cancellablePromise);
    pr.finally(() => {
      this._promises.delete(pr);
    });
    return cancellablePromise;
  }

  dispose() {
    this._promises.forEach(pr => pr.cancel());
    this._promises.clear();
  }
}

export interface CancellablePromise<T> extends Promise<T> {
  cancel():void;
}

export class WrappedCancellablePromise<T> implements CancellablePromise<T> {
  private promise:Promise<T>;
  private cancelled:boolean;

  constructor(promise:Promise<T>) {
    this.cancelled = false;
    this.promise = promise;
  }

  readonly [Symbol.toStringTag]: string;

  then<TResult1 = T, TResult2 = never>(onFulfilled?: (SuccessCallback<T, TResult1, TResult2>) | undefined | null,
                                       onRejected?: (ErrorCallback<TResult2>) | undefined | null): Promise<TResult1 | TResult2> {
    let wrappedOnFulfilled:(SuccessCallback<T, TResult1, TResult2>) | undefined | null = undefined;
    if(onFulfilled) {
      wrappedOnFulfilled = (value:T) => {
        if(this.cancelled) {
          return new Promise<TResult1>(() => {});
        }
        return onFulfilled(value);
      };
    }
    let wrappedOnRejected:((ErrorCallback<TResult2>) | undefined | null) = undefined;
    if(onRejected) {
      wrappedOnRejected = (err:any) => {
        if(this.cancelled) {
          return new Promise<TResult2>(() => {});
        }
        return onRejected(err);
      };
    }
    return this.promise.then(wrappedOnFulfilled, wrappedOnRejected);
  }

  catch<TResult = never>(onRejected?: (ErrorCallback<TResult>) | undefined | null): Promise<T | TResult> {
    let wrappedOnRejected:((ErrorCallback<TResult>) | undefined | null) = undefined;
    if(onRejected) {
      wrappedOnRejected = (err:any) => {
        if(this.cancelled) {
          return new Promise<TResult>(() => {});
        }
        return onRejected(err);
      };
    }
    return this.promise.catch(wrappedOnRejected);
  }

  finally(onFinally?: (() => void) | undefined | null): Promise<T> {
    let wrappedOnFinally:(() => void) | undefined | null = undefined;
    if(onFinally) {
      wrappedOnFinally = () => {
        if(this.cancelled) {
          return new Promise<T>(() => {});
        }
        return onFinally();
      };
    }
    return this.promise.finally(wrappedOnFinally);
  }

  cancel() {
    this.cancelled = true;
  }
}